// Best-effort mapping from Flarum tag id to the GBIF "highertaxon" group
// used to constrain /api/species-autocomplete (see
// SpeciesAutocompleteService::TAXON_QUERIES on the PHP side: fungi, myxo,
// plants, insects). null means "identification-relevant but no taxon
// constraint" (e.g. "Clés de détermination" covers every group). Tags not
// listed here don't get the species-assignment control at all - species
// tagging doesn't make sense on e.g. "Achat / Vente" or "Livres", and was
// explicitly dropped for "Autres sujets sur la mycologie" too.
//
// "Champignons" (1000017) - the parent of most tags below - is deliberately
// left out despite being the one tag most of their own discussions also
// carry: every child forum inherits it, including several with nothing to
// do with identification ("Autres sujets sur la mycologie", "Les
// champignons dans la cuisine", "Microscopie et réactifs", "Liens utiles",
// "Photographier les champignons") - confirmed live, each one's own
// discussions all carry "Champignons" too. Listing the parent here
// (relevantTags() below checks *any* of a discussion's tags) matched those
// unrelated forums as well, via that shared tag alone, the exact bug this
// file's own EXCLUDED_TAG_IDS below already works around for one specific
// case ("Evénements..."). No discussion is tagged with only "Champignons"
// and nothing more specific, so dropping it doesn't lose any genuinely
// relevant one either - each still qualifies through its own tag below.
export const TAG_TAXON_MAP = {
  1000001: 'fungi',
  // Détermination de champignons
  1000028: 'fungi',
  // Lichens (no dedicated GBIF group)
  1000036: 'myxo',
  // Myxomycètes
  1000031: 'plants',
  // Plantes
  1000004: 'insects',
  // Insectes (et autres arthropodes...)
  1000032: null,
  // Autres (Nature) - unconstrained, any group
  1000016: 'fungi',
  // Jeu du Qui suis-je ? - fungi only
  1000082: null,
  // Clés de détermination - unconstrained, multi-species
  1000046: 'fungi',
  // Galerie photos Champignons
  1000047: 'myxo',
  // Galerie photos Myxomycètes
  1000048: 'fungi',
  // Galerie photos Lichens
  1000060: 'fungi',
  // Descriptions
  1000063: 'fungi' // Aquarelles
};
export const MULTI_SPECIES_TAG_ID = 1000082;

// Tags that must never get species assignment, even though one of the
// discussion's *other* tags matches TAG_TAXON_MAP above - namely
// "Evénements et expositions mycologiques" (1000034), whose parent tag is
// "Champignons" (1000017, itself in TAG_TAXON_MAP as 'fungi'). Every
// discussion in that forum carries both, so checking the whole tag set
// naively made every event discussion count as identification-relevant
// too. That tag has its own dedicated structured data instead (dates,
// place, url - see champis-forum's addEventControl.js/EVENTS_TAG_ID).
const EXCLUDED_TAG_IDS = [1000034];
function relevantTags(tags) {
  const list = (tags || []).filter(t => !!t);
  if (list.some(t => EXCLUDED_TAG_IDS.includes(Number(t.id())))) {
    return [];
  }
  return list.filter(t => Object.prototype.hasOwnProperty.call(TAG_TAXON_MAP, String(t.id())));
}

/**
 * Whether at least one of the discussion's (or pending composer) tags is
 * identification-relevant - gates whether the "Espèce" control shows at
 * all. Distinct from Discussion.canAssignSpecies() (server-computed,
 * author/mod/admin permission check) - both must pass for the control to
 * actually show.
 */
export function hasIdentificationTag(tags) {
  return relevantTags(tags).length > 0;
}

// "Détermination de champignons" and "Jeu du Qui suis-je ?" are both
// identification/guessing forums where figuring out the species *is* the
// point of the discussion - it isn't known yet when the topic starts, so
// species can only be assigned afterwards (discussion controls), never
// from the composer at creation time.
const COMPOSER_EXCLUDED_TAG_IDS = [1000001, 1000016];

/**
 * Same gate as hasIdentificationTag(), but for the composer specifically -
 * used only by addSpeciesComposer.js. addSpeciesControl.js (editing an
 * already-created discussion) keeps using hasIdentificationTag() directly,
 * since COMPOSER_EXCLUDED_TAG_IDS only restricts *creation-time* assignment.
 */
export function hasIdentificationTagAtCreation(tags) {
  const list = (tags || []).filter(t => !!t);
  if (list.some(t => COMPOSER_EXCLUDED_TAG_IDS.includes(Number(t.id())))) {
    return false;
  }
  return hasIdentificationTag(tags);
}

/**
 * @returns a highertaxon value to constrain the search to, or undefined
 * for no constraint (mixed tags, or an explicitly unconstrained tag like
 * "Clés de détermination" or "Autres" take priority).
 */
export function resolveHighertaxon(tags) {
  const relevant = relevantTags(tags);
  if (relevant.some(t => TAG_TAXON_MAP[String(t.id())] === null)) {
    return undefined;
  }
  return relevant.map(t => TAG_TAXON_MAP[String(t.id())]).find(Boolean);
}
export function allowsMultipleSpecies(tags) {
  return (tags || []).some(t => t && String(t.id()) === String(MULTI_SPECIES_TAG_ID));
}

// "Jeu du Qui suis-je ?" is a guessing game - nagging the author to reveal
// the species the moment they post defeats the point, so the reminder is
// delayed by 24h there instead of showing immediately like every other
// identification-relevant forum.
const REMINDER_DELAY_TAG_ID = 1000016;
const REMINDER_DELAY_MS = 24 * 60 * 60 * 1000;
export function reminderDelayElapsed(discussion, tags) {
  const list = (tags || []).filter(t => !!t);
  if (!list.some(t => String(t.id()) === String(REMINDER_DELAY_TAG_ID))) {
    return true;
  }
  const createdAt = discussion.createdAt && discussion.createdAt();
  return !!createdAt && Date.now() - createdAt.getTime() >= REMINDER_DELAY_MS;
}
flarum.reg.add('champis-species', 'forum/utils/taxonGroups', { TAG_TAXON_MAP: TAG_TAXON_MAP,MULTI_SPECIES_TAG_ID: MULTI_SPECIES_TAG_ID,hasIdentificationTag: hasIdentificationTag,hasIdentificationTagAtCreation: hasIdentificationTagAtCreation,resolveHighertaxon: resolveHighertaxon,allowsMultipleSpecies: allowsMultipleSpecies,reminderDelayElapsed: reminderDelayElapsed, });